import * as yup from 'yup';
import { PTOType } from '@/types/api';
import { Months, getYearsFromNowList, getFormatedDate } from './dates';
import { getVisibleRichText } from './format';
import { getBusinessDates } from './pto';
import { isUrlValid } from './url';

export const requiredMessage = 'This field is required.';
export const incorrectUrlMessage = 'Enter a valid url.';
export const invalidEmailMessage = 'Invalid e-mail address.';
const atLeastOneOption = 'Select at least 1 option.';
const dropdownMessage = 'Please select an option.';
const invalidMonth = 'Please select a valid month.';
const invalidYear = 'Please select a valid year.';

export const candidateProjectFormSchema = yup.object().shape({
  title: yup.string().required(requiredMessage),
  link: yup
    .string()
    .test('check-url-valid', incorrectUrlMessage, (url = '') => (url ? isUrlValid(url) : true)),
  description: yup.string().required(requiredMessage),
  year: yup
    .string()
    .required(requiredMessage)
    .test('checking-year-value', requiredMessage, (v) => {
      return v !== 'Select a year';
    }),
  skills: yup.array().min(1, atLeastOneOption).required(requiredMessage),
});

export const workExperienceSchema = yup.object().shape({
  title: yup.string().required(requiredMessage),
  startMonth: yup
    .mixed<keyof typeof Months>()
    .oneOf(Object.values(Months))
    .required(dropdownMessage),
  startYear: yup.string().required(dropdownMessage),
  endMonth: yup.string().when('isCurrentWork', (isCurrentWork) => {
    if (!isCurrentWork) {
      return yup
        .mixed<keyof typeof Months>()
        .oneOf(Object.values(Months), dropdownMessage)
        .required(dropdownMessage);
    }

    return yup.string().nullable();
  }),
  endYear: yup.string().when('isCurrentWork', (isCurrentWork) => {
    if (!isCurrentWork) {
      return yup.string().required(dropdownMessage);
    }

    return yup.string().nullable();
  }),
  company: yup.string().required(requiredMessage),
  description: yup.string().nullable(),
  location: yup.string().required(requiredMessage),
  isCurrentWork: yup.boolean().nullable(),
  techs: yup.array().of(yup.string()).min(1, atLeastOneOption).required(requiredMessage),
});

export const educationSchema = yup.object().shape({
  university: yup.string().required(requiredMessage),
  degree: yup.string().required(requiredMessage),
  endMonth: yup.mixed<keyof typeof Months>().when('currentlyPursuing', {
    is: false,
    then: (schema) => schema.oneOf(Object.values(Months), invalidMonth),
  }),
  endYear: yup.string().when('currentlyPursuing', {
    is: false,
    then: (schema) => schema.oneOf(getYearsFromNowList(), invalidYear),
  }),
  startMonth: yup
    .mixed<keyof typeof Months>()
    .oneOf(Object.values(Months), invalidMonth)
    .required(requiredMessage),
  startYear: yup.string().oneOf(getYearsFromNowList(), invalidYear).required(requiredMessage),
  currentlyPursuing: yup.boolean(),
});

export const buildErrorMessage = (isTouched, hasError): string => {
  return !!isTouched && !!hasError ? hasError : '';
};

export const scrollToFieldWithError = (
  formContainerClassName: string,
  errorMessageClassName = 'error-message'
): void => {
  setTimeout(() => {
    const newJobForm = document.querySelector(`.${formContainerClassName}`);
    const firstError = document.querySelector(`.${errorMessageClassName}`);

    if (newJobForm && firstError) {
      const formScroll = newJobForm.scrollTop;
      const formDistanceToTop = newJobForm.getBoundingClientRect().top;
      const errorPosition = firstError.getBoundingClientRect().top;
      const inputHeight = 56;

      const scrollTo = formScroll + errorPosition - formDistanceToTop - inputHeight;
      newJobForm.scrollTo({
        top: scrollTo,
        behavior: 'smooth',
      });
    }
  }, 100);
};

export const submitTechnicalChallengeModalFormSchema = yup.object({
  githubLink: yup.string().required(requiredMessage).url(incorrectUrlMessage),
  title: yup.string().required(requiredMessage),
  technicalDesign: yup
    .string()
    .test({
      message: requiredMessage,
      test: (text) => Boolean(getVisibleRichText(text || '')),
    })
    .required(requiredMessage),
  videoPresentation: yup
    .mixed<File | null>()
    .test({
      message: requiredMessage,
      test: (file) => Boolean(file),
    })
    .test({
      message: 'Invalid file extension',
      test: (file) => {
        return (file?.type || '').startsWith('video/');
      },
    })
    .required(),
});

export const requestTimeOffFormSchema = yup.object({
  category: yup.mixed<PTOType>().required(requiredMessage),
  caregiverStatus: yup
    .mixed<PTOType.PRIMARY_CAREGIVER | PTOType.SECONDARY_CAREGIVER>()
    .when('category', {
      is: (value?: PTOType) =>
        value === PTOType.PRIMARY_CAREGIVER || value === PTOType.SECONDARY_CAREGIVER,
      then: (schema) => schema.required(requiredMessage),
    }),
  from: yup.date().required(requiredMessage),
  to: yup.date().required(requiredMessage),
  quantities: yup.mixed<Record<string, string>>().test({
    message: 'All quantities must be filled',
    test(value) {
      const from = this.parent.from as Date | undefined;
      const to = this.parent.to as Date | undefined;

      if (from && to) {
        const dateIntervals = getBusinessDates(from, to) || [];
        const dateKeys = dateIntervals.map((date) => getFormatedDate(date));
        return !dateKeys.some((key) => !value?.[key]);
      }

      return true;
    },
  }),
  note: yup.string(),
});
